/**
 * Cards are plain integers 0..51 so they serialize cheaply over WebSockets.
 *   rank = card % 13   (0 = deuce ... 12 = ace)
 *   suit = floor(card / 13)   (0 = clubs, 1 = diamonds, 2 = hearts, 3 = spades)
 */
export type Card = number;

export const RANK_CHARS = "23456789TJQKA";
export const SUIT_CHARS = "cdhs";

export const rankOf = (c: Card): number => c % 13;
export const suitOf = (c: Card): number => Math.floor(c / 13);

export function cardToString(c: Card): string {
  return RANK_CHARS[rankOf(c)]! + SUIT_CHARS[suitOf(c)]!;
}

export function parseCard(s: string): Card {
  const r = RANK_CHARS.indexOf(s[0]!.toUpperCase());
  const su = SUIT_CHARS.indexOf(s[1]!.toLowerCase());
  if (s.length !== 2 || r < 0 || su < 0) throw new Error(`Bad card: ${s}`);
  return su * 13 + r;
}

/** "As Kd 7h" -> [card, card, card] */
export function parseCards(s: string): Card[] {
  return s.trim().split(/\s+/).filter(Boolean).map(parseCard);
}

/** Returns an integer in [0, maxExclusive). */
export type RandomInt = (maxExclusive: number) => number;

/**
 * Cryptographically secure, unbiased random integer (rejection sampling).
 * Uses the Web Crypto global, which exists in Node 20+, browsers and Cloudflare Workers.
 */
export const secureRandomInt: RandomInt = (max) => {
  if (!Number.isInteger(max) || max <= 0 || max > 0x100000000) {
    throw new Error(`secureRandomInt: bad max ${max}`);
  }
  const limit = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);
  let x: number;
  do {
    crypto.getRandomValues(buf);
    x = buf[0]!;
  } while (x >= limit);
  return x % max;
};

export function newDeck(): Card[] {
  return Array.from({ length: 52 }, (_, i) => i);
}

/** Fisher-Yates shuffle; returns a new array. */
export function shuffle(deck: readonly Card[], rand: RandomInt = secureRandomInt): Card[] {
  const a = [...deck];
  for (let i = a.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}
